import React, {
  memo,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import ButtonComponent from '../../components/ButtonMC';
import useCallPermissionActions from '../../hooks/fonema/actions/useCallPermissionActions';
import PermissionRequestDialog from './PermissionRequestDialog';
import useFonemaCallBusy from '../../hooks/fonema/useFonemaCallBusy';
import useCallProvider from '../../hooks/calls/useCallProvider';
import { isWhatsappCallsUiEnabledForProvider } from '../../hooks/calls/resolveCallProvider';
import { sharingInformationService } from '../../../services/sharingInformationService';
import { canUserMakeCalls } from '../../utils/callsPermissionsUtils';

/**
 * Icon button to initiate a WhatsApp call or request call permission (inbox header style).
 * Voice multi-line: opens the outbound dialer so permission is checked for the selected line.
 * Fonema / Voice single line: pre-checks permission and shows call vs request-permission.
 */
const CallButton = memo(({ customer }) => {
  const { t } = useTranslation('Fonema');
  const provider = useCallProvider();
  const whatsappCallsEnabled = isWhatsappCallsUiEnabledForProvider(provider);
  const currentRetailerUser = useSelector((state) => state.mainReducer?.currentRetailerUser);

  const [isPermissionDialogOpen, setIsPermissionDialogOpen] = useState(false);
  const [permissionStatus, setPermissionStatus] = useState(null);

  const callUiBusy = useFonemaCallBusy();

  const {
    checkPermission,
    requestPermission,
    requestingPermission,
    mustConfirmFrom,
    outboundLinesLoading,
    isVoiceProvider
  } = useCallPermissionActions();

  // Keep the latest `checkPermission` without making it an effect dependency: its identity
  // changes as the outbound lines resolve (defaultLineScope → resolveLineScope), which would
  // otherwise fire the permission probe twice on the same customer.
  const checkPermissionRef = useRef(checkPermission);
  checkPermissionRef.current = checkPermission;

  const canMakeCalls = canUserMakeCalls(currentRetailerUser);
  // Voice + more than one SIP-ready line → defer permission until the dialer selector.
  const deferPermissionToDialer = isVoiceProvider && mustConfirmFrom;
  const waitingOutboundLines = isVoiceProvider && outboundLinesLoading;

  const startCall = useCallback(() => {
    sharingInformationService.setSubject({
      InitiateCall: true,
      customer
    });
    sharingInformationService.setSubject({});
  }, [customer]);

  useEffect(() => {
    // Wait until SIP-ready lines are known so we do not pre-check with the wrong scope.
    if (!whatsappCallsEnabled || waitingOutboundLines || deferPermissionToDialer) {
      setPermissionStatus(null);
      return undefined;
    }

    if (!(customer?.phone_with_country_code || customer?.phone)) {
      setPermissionStatus(null);
      return undefined;
    }

    let isActive = true;

    const isCheckingPermission = async () => {
      setPermissionStatus(null);
      const result = await checkPermissionRef.current(customer);
      if (!isActive) return;
      setPermissionStatus(result?.canStartCall || false);
    };

    isCheckingPermission();

    return () => {
      isActive = false;
    };
  }, [
    customer,
    whatsappCallsEnabled,
    waitingOutboundLines,
    deferPermissionToDialer
  ]);

  const handleRequestPermission = useCallback(async () => {
    const success = await requestPermission(customer);
    if (!success) return;

    setIsPermissionDialogOpen(false);
  }, [customer, requestPermission]);

  const handleClick = useCallback(() => {
    if (deferPermissionToDialer) {
      startCall();
      return;
    }
    if (permissionStatus) {
      startCall();
      return;
    }
    setIsPermissionDialogOpen(true);
  }, [deferPermissionToDialer, permissionStatus, startCall]);

  if (!whatsappCallsEnabled || !canMakeCalls) {
    return null;
  }

  if (callUiBusy || requestingPermission || waitingOutboundLines) {
    return null;
  }

  // Single / legacy: wait for permission probe. Multi: show call affordance immediately.
  if (!deferPermissionToDialer && permissionStatus === null) {
    return null;
  }

  const tooltipContent = deferPermissionToDialer || permissionStatus
    ? t('call')
    : t('requestPermissionText');
  const callIcon = deferPermissionToDialer || permissionStatus ? 'phone-outline' : 'lock-outline';
  const callIconClassName = deferPermissionToDialer || permissionStatus
    ? 'tw-text-green-600'
    : 'tw-text-blue-500';

  return (
    <>
      <ButtonComponent
        id={deferPermissionToDialer || permissionStatus ? 'call-action' : 'call-request-permission-action'}
        outlined
        variant="GRAY"
        icon={callIcon}
        size="medium"
        className="tw-h-[40px] tw-shrink-0 tw-text-default"
        iconClassName={callIconClassName}
        data-tooltip-id="tooltip-call-action"
        data-tooltip-content={tooltipContent}
        onClick={handleClick}
      />
      {isPermissionDialogOpen && (
        <PermissionRequestDialog
          customer={customer}
          onClose={() => setIsPermissionDialogOpen(false)}
          onRequestPermission={handleRequestPermission}
        />
      )}
    </>
  );
});

CallButton.displayName = 'CallButton';

export default CallButton;
